import { Suspense, lazy, useEffect } from "react";
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";
import { Toaster } from "sonner";
import { AuthWrapper, RootElement } from "./components/AppShell";
import LoginForm from "./components/LoginForm";
import { TooltipProvider } from "./components/ui/tooltip";
import { usePushBootstrap } from "./hooks/useNotification";
import App from "./pages/App";
import { useDark } from "./store/dark";
import { useLayoutStore } from "./store/layout";
import { useUserStore } from "./store/user";

const Test = lazy(() => import("./pages/Test"));
const Register = lazy(() => import("./pages/Register"));
const DevicesPage = lazy(() => import("./pages/DevicesPage"));
const BmsDetail = lazy(() => import("./pages/BmsDetail"));
const BmsDebug = lazy(() => import("./pages/BmsDebug"));
const PipDetail = lazy(() => import("./pages/PipDetail"));
const PipDebug = lazy(() => import("./pages/PipDebug"));
const TracerDetail = lazy(() => import("./pages/TracerDetail"));
const TracerDebug = lazy(() => import("./pages/TracerDebug"));
const AutomationsPage = lazy(() => import("./pages/AutomationsPage"));
const AutomationEditorPage = lazy(() => import("./pages/AutomationEditorPage"));
const SystemPage = lazy(() => import("./pages/SystemPage"));

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <RootElement>
        <AuthWrapper />
      </RootElement>
    ),
    children: [
      { index: true, element: <App /> },
      {
        path: "devices",
        element: (
          <Suspense>
            <DevicesPage />
          </Suspense>
        ),
      },
      {
        path: "bms/:slug",
        element: (
          <Suspense>
            <BmsDetail />
          </Suspense>
        ),
      },
      {
        path: "bms/:slug/debug",
        element: (
          <Suspense>
            <BmsDebug />
          </Suspense>
        ),
      },
      {
        path: "pip/:slug",
        element: (
          <Suspense>
            <PipDetail />
          </Suspense>
        ),
      },
      {
        path: "pip/:slug/debug",
        element: (
          <Suspense>
            <PipDebug />
          </Suspense>
        ),
      },
      {
        path: "tracer/:slug",
        element: (
          <Suspense>
            <TracerDetail />
          </Suspense>
        ),
      },
      {
        path: "tracer/:slug/debug",
        element: (
          <Suspense>
            <TracerDebug />
          </Suspense>
        ),
      },
      {
        path: "automations",
        element: (
          <Suspense>
            <AutomationsPage />
          </Suspense>
        ),
      },
      {
        path: "automations/:id",
        element: (
          <Suspense>
            <AutomationEditorPage />
          </Suspense>
        ),
      },
      {
        // ADMIN-only stránka; gate řeší komponenta (a hlavně server), ne router —
        // žádná route v aplikaci dnes loader ani errorElement nemá.
        path: "system",
        element: (
          <Suspense>
            <SystemPage />
          </Suspense>
        ),
      },
      {
        path: "test",
        element: (
          <Suspense>
            <Test />
          </Suspense>
        ),
      },
      ...(import.meta.env.MODE === "development"
        ? [
            {
              path: "register",
              element: (
                <Suspense>
                  <Register />
                </Suspense>
              ),
            },
          ]
        : []),
      {
        path: "login",
        element: (
          <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
            <LoginForm />
          </div>
        ),
      },
    ],
  },
]);

/**
 * Root - jediný "Provider" je TooltipProvider (Radix kontextu pro tooltipy).
 * Žádný Provider Hell, žádné Context API. Všechen state je v Zustand stores,
 * dark mode je aplikován přes side effect níže.
 */
export const Root = () => {
  const dark = useDark();

  usePushBootstrap();

  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add("dark");
    else root.classList.remove("dark");
  }, [dark]);

  useEffect(() => {
    useUserStore.getState().fetchInitial();
    void useLayoutStore.getState().load();
  }, []);

  return (
    <TooltipProvider delayDuration={200}>
      <Toaster richColors position="top-right" closeButton />
      <RouterProvider router={router} />
    </TooltipProvider>
  );
};

export default Root;
